import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, BatteryCharging, FileCheck2, Package, MapPin, Phone, ArrowLeft, Check } from "lucide-react";
import { getProductBySlug, formatPrice } from "@/data/products";
import { business } from "@/config/business";
import { ProductWhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { useSeo } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

export default function ProductDetails() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  useSeo({
    title: product ? `${product.brandName} ${product.model} ${product.storage} — Mobile Specialist Rohini` : "Phone — Mobile Specialist",
    description: product ? `${product.brandName} ${product.model}, ${product.storage}, ${product.color}. ${product.type === "used" ? "Pre-owned" : "New"} at Mobile Specialist, Rohini. ${formatPrice(product.price)}. Enquire on WhatsApp.` : "",
    path: `/phones/${slug}`,
    image: product?.image,
  });

  if (!product) return <NotFound />;
  const u = product.used;

  const specs = [
    ["Brand", product.brandName],
    ["Model", product.model],
    ["Storage", product.storage],
    ["Colour", product.color],
    ["Type", product.type === "new" ? "New" : "Pre-Owned"],
    u && ["Condition", u.condition],
    u && ["Battery Health", `${u.batteryHealth}%`],
    u && ["Warranty", u.warrantyMonths ? `${u.warrantyMonths} months` : "—"],
    u && ["Original Bill", u.billAvailable ? "Available" : "Not available"],
    ["Availability", product.availability],
  ].filter(Boolean);

  return (
    <div className="container-wide py-10" data-testid="product-details">
      <Link to="/phones" className="mb-6 inline-flex items-center gap-1.5 text-sm text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"><ArrowLeft className="h-4 w-4" /> Back to phones</Link>

      <div className="grid gap-10 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.22,1,0.36,1] }}
          className="relative flex items-center justify-center rounded-3xl border border-border bg-card p-8">
          <span className="absolute left-4 top-4 chip">{product.type === "new" ? "New" : "Pre-Owned"}</span>
          <img src={product.image} alt={`${product.brandName} ${product.model} in ${product.color}`} className="max-h-[460px] w-full object-contain" />
        </motion.div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--ink-soft))]">{product.brandName}</p>
          <h1 className="mt-2 font-display text-4xl font-bold">{product.model}</h1>
          <p className="mt-1 text-[hsl(var(--ink-soft))]">{product.storage} · {product.color}</p>

          {u && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="used-badges">
              <span className="chip"><ShieldCheck className="h-3.5 w-3.5 accent-text" /> {u.condition} Condition</span>
              <span className="chip"><BatteryCharging className="h-3.5 w-3.5 accent-text" /> {u.batteryHealth}% Battery</span>
              {u.warrantyMonths ? <span className="chip">{u.warrantyMonths} Month Warranty</span> : null}
              {u.billAvailable ? <span className="chip"><FileCheck2 className="h-3.5 w-3.5 accent-text" /> Bill Available</span> : null}
            </div>
          )}

          <p className="mt-6 text-3xl font-bold">{formatPrice(product.price)}</p>
          <p className="mt-1 text-xs text-[hsl(var(--ink-soft))]">Demo price for preview — confirmed in store or on WhatsApp.</p>

          <p className="mt-6 leading-relaxed text-[hsl(var(--ink-soft))]">{product.description}</p>

          {product.accessories?.length ? (
            <div className="mt-5">
              <p className="text-sm font-semibold">Accessories included</p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {product.accessories.map((a) => <li key={a} className="chip"><Check className="h-3.5 w-3.5 accent-text" /> {a}</li>)}
              </ul>
            </div>
          ) : null}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <ProductWhatsAppButton product={product} full />
            <a href={`tel:${business.phone}`} className="btn-outline w-full" data-testid="call-store"><Phone className="h-4 w-4" /> Call Store</a>
          </div>
          <p className="mt-4 flex items-center gap-2 text-sm text-[hsl(var(--ink-soft))]"><MapPin className="h-4 w-4 accent-text" /> Available at Mobile Specialist, Sector 3 Rohini</p>
        </div>
      </div>

      <div className="mt-14 max-w-2xl">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold"><Package className="h-5 w-5" /> Specifications</h2>
        <dl className="mt-5 overflow-hidden rounded-2xl border border-border">
          {specs.map(([k, v], i) => (
            <div key={k} className={`flex justify-between px-5 py-3.5 text-sm ${i % 2 ? "bg-card" : "bg-[hsl(var(--paper))]"}`}>
              <dt className="text-[hsl(var(--ink-soft))]">{k}</dt><dd className="font-medium">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
